import React from 'react';
import { Box, IconButton, Tooltip, CircularProgress } from '@mui/material';
import PermissionGate from '../common/PermissionGate';
import {
  AutoFixHigh as ImproveIcon,
  Info as ExplainIcon,
  ContentCopy as CopyIcon,
  DeleteOutline as DeleteIcon,
  Edit as EditIcon,
  Save as SaveEditIcon,
  Cancel as CancelIcon,
  RestartAlt,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  FolderZip as DownloadIcon,
  UnfoldMore as ExpandHeightIcon,
  UnfoldLess as CollapseHeightIcon,
  OpenInFull as PopupIcon,
  Lock as LockIcon,
  ChatBubbleOutline as CommentIcon
} from '@mui/icons-material';

const ActionButtons = ({
  onImprove,
  onExplain,
  onCopy,
  onDelete,
  onEdit,
  onSave,
  onCancel,
  onZoomIn,
  onZoomOut,
  onDownload,
  isDownloading = false,
  item,
  categoryIndex,
  itemIndex,
  vertical = false,
  editingFeature,
  featureId,
  editingStory,
  storyId,
  isSaving = false,
  showZoomControls = false,
  zoomLevel = 1,
  minZoom = 0.5,
  maxZoom = 2.0,
  onExpandHeight,
  isHeightExpanded = false,
  onOpenPopup,
  isApproved = false,
  approvedByType = null, // 'internal' | 'external' | null
  onComment,
  commentCount = 0
}) => {
  if (vertical) {
    // Check if we're in edit mode (either for features or stories)
    const isEditing = (editingFeature && featureId && editingFeature === featureId) || 
                      (editingStory && storyId && editingStory === storyId);
    
    return (
      <>
        {/* Edit mode buttons */}
        {isEditing ? (
          <>
            {/* Save button */}
            <Box sx={{ 
              mb: 1,
              width: 32,
              height: 32,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: '#f0f0f0',
              borderRadius: '4px',
            }}>
              <Tooltip title={isSaving ? "Saving..." : "Save"} placement="left">
                <span>
                  <IconButton
                    size="small"
                    sx={{ p: 0.5 }}
                    color="success"
                    disabled={isSaving}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onSave) onSave();
                    }}
                  >
                    {isSaving ? (
                      <RestartAlt fontSize="small" sx={{ fontSize: '1rem' }} />
                    ) : (
                      <SaveEditIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            {/* Cancel button */}
            <Box sx={{ 
              mb: 1,
              width: 32,
              height: 32,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: '#f0f0f0',
              borderRadius: '4px',
            }}>
              <Tooltip title="Cancel" placement="left">
                <IconButton
                  size="small"
                  sx={{ p: 0.5 }}
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onCancel) onCancel();
                  }}
                >
                  <CancelIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                </IconButton>
              </Tooltip>
            </Box>
          </>
        ) : (
          <>
            {/* Zoom controls - only for diagrams */}
            {showZoomControls && (
              <>
                <Box sx={{
                  mb: 1,
                  width: 32,
                  height: 32,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: '#f0f0f0',
                  borderRadius: '4px',
                }}>
                  <Tooltip title="Zoom in" placement="left">
                    <span>
                      <IconButton
                        size="small"
                        sx={{ p: 0.5 }}
                        disabled={zoomLevel >= maxZoom}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onZoomIn) onZoomIn();
                        }}
                      >
                        <ZoomInIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
                <Box sx={{
                  mb: 1,
                  width: 32,
                  height: 32,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: '#f0f0f0',
                  borderRadius: '4px',
                }}>
                  <Tooltip title="Zoom out" placement="left">
                    <span>
                      <IconButton
                        size="small"
                        sx={{ p: 0.5 }}
                        disabled={zoomLevel <= minZoom}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onZoomOut) onZoomOut();
                        }}
                      >
                        <ZoomOutIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </>
            )}

            {/* Edit button — only locked when approved by EXTERNAL validator */}
            {(() => {
              const isLockedByExternal = isApproved && approvedByType === 'external';
              if (isLockedByExternal && onEdit) {
                return (
                  <Box sx={{
                    mb: 1, width: 32, height: 32,
                    display: 'flex', justifyContent: 'center', alignItems: 'center',
                    backgroundColor: '#e8f5e9', borderRadius: '4px',
                  }}>
                    <Tooltip title="Approved by external validator — editing locked" placement="left">
                      <LockIcon fontSize="small" sx={{ fontSize: '1rem', color: '#2e7d32' }} />
                    </Tooltip>
                  </Box>
                );
              }
              if (onEdit) {
                return (
                  <PermissionGate permission="conversation.content.edit">
                    <Box sx={{
                      mb: 1, width: 32, height: 32,
                      display: 'flex', justifyContent: 'center', alignItems: 'center',
                      backgroundColor: isApproved ? '#fff3e0' : '#f0f0f0',
                      borderRadius: '4px',
                    }}>
                      <Tooltip title={isApproved ? "Edit (internally validated — still editable)" : "Edit"} placement="left">
                        <IconButton
                          size="small"
                          sx={{ p: 0.5 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onEdit) onEdit();
                          }}
                        >
                          <EditIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </PermissionGate>
                );
              }
              return null;
            })()}

            {/* Improve button - temporarily hidden */}
            {false && onImprove && (
              <Box sx={{
                mb: 1,
                width: 32,
                height: 32,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: '#f0f0f0',
                borderRadius: '4px',
              }}>
                <Tooltip title="Improve" placement="left">
                  <IconButton
                    size="small"
                    sx={{ p: 0.5 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onImprove) onImprove(item, categoryIndex, itemIndex);
                    }}
                  >
                    <ImproveIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </Tooltip>
              </Box>
            )}

            {/* Explain button - only show if handler provided */}
            {onExplain && (
              <Box sx={{
                mb: 1,
                width: 32,
                height: 32,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: '#f0f0f0',
                borderRadius: '4px',
              }}>
                <Tooltip title="Explain" placement="left">
                  <IconButton
                    size="small"
                    sx={{ p: 0.5 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onExplain) onExplain(item, categoryIndex, itemIndex);
                    }}
                  >
                    <ExplainIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </Tooltip>
              </Box>
            )}

            {/* Comment button */}
            {onComment && (
              <Box sx={{
                mb: 1,
                width: 32,
                height: 32,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: commentCount > 0 ? '#fff3e0' : '#f0f0f0',
                borderRadius: '4px',
                position: 'relative',
              }}>
                <Tooltip title={commentCount > 0 ? `${commentCount} comment${commentCount > 1 ? 's' : ''}` : "Add comment"} placement="left">
                  <IconButton
                    size="small"
                    sx={{ p: 0.5, color: commentCount > 0 ? '#e65100' : undefined }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onComment();
                    }}
                  >
                    <CommentIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </Tooltip>
                {commentCount > 0 && (
                  <Box sx={{
                    position: 'absolute', top: -4, right: -4,
                    width: 16, height: 16, borderRadius: '50%',
                    bgcolor: '#e65100', color: '#fff',
                    fontSize: 9, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {commentCount > 9 ? '9+' : commentCount}
                  </Box>
                )}
              </Box>
            )}

            {/* Copy button */}
            {onCopy && (
              <Box sx={{
                mb: 1,
                width: 32,
                height: 32,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: '#f0f0f0',
                borderRadius: '4px',
              }}>
                <Tooltip title="Copy" placement="left">
                  <IconButton
                    size="small"
                    sx={{ p: 0.5 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onCopy) onCopy(item, categoryIndex, itemIndex);
                      else {
                        navigator.clipboard.writeText(
                          JSON.stringify(item, null, 2)
                        );
                      }
                    }}
                  >
                    <CopyIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </Tooltip>
              </Box>
            )}

            {/* Download button */}
            {onDownload && (
              <PermissionGate permission="conversation.content.download">
                <Box sx={{
                  mb: 1,
                  width: 32,
                  height: 32,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: '#f0f0f0',
                  borderRadius: '4px',
                }}>
                  <Tooltip title="Download feature with related content" placement="left">
                    <span>
                      <IconButton
                        size="small"
                        sx={{ p: 0.5 }}
                        disabled={isDownloading}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onDownload) onDownload(item);
                        }}
                      >
                        {isDownloading ? (
                          <CircularProgress size={16} />
                        ) : (
                          <DownloadIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </PermissionGate>
            )}

            {/* Delete button — hidden when section is approved */}
            {onDelete && !isApproved && (
              <PermissionGate permission="conversation.content.delete">
                <Box sx={{
                  width: 32,
                  height: 32,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: '#f0f0f0',
                  borderRadius: '4px',
                  mb: 1,
                }}>
                  <Tooltip title="Delete" placement="left">
                    <IconButton
                      size="small"
                      sx={{ p: 0.5 }}
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onDelete) onDelete(item, categoryIndex, itemIndex);
                      }}
                    >
                      <DeleteIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </PermissionGate>
            )}

            {/* Expand/Collapse height button */}
            {onExpandHeight && (
              <Box sx={{
                mb: 1,
                width: 32,
                height: 32,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: '#f0f0f0',
                borderRadius: '4px',
              }}>
                <Tooltip title={isHeightExpanded ? "Collapse diagram height" : "Expand to full height"} placement="left">
                  <IconButton
                    size="small"
                    sx={{ p: 0.5 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onExpandHeight();
                    }}
                  >
                    {isHeightExpanded
                      ? <CollapseHeightIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                      : <ExpandHeightIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                    }
                  </IconButton>
                </Tooltip>
              </Box>
            )}

            {/* Open in popup button */}
            {onOpenPopup && (
              <Box sx={{
                width: 32,
                height: 32,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: '#f0f0f0',
                borderRadius: '4px',
                mb: 1,
              }}>
                <Tooltip title="Open in popup" placement="left">
                  <IconButton
                    size="small"
                    sx={{ p: 0.5 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenPopup();
                    }}
                  >
                    <PopupIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </>
        )}
      </>
    );
  }
  
  return (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'row',
      gap: 1
    }}>
      {/* Improve button - temporarily hidden */}
      {false && <Tooltip title="Improve" placement="top">
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            if (onImprove) onImprove(item, categoryIndex, itemIndex);
          }}
        >
          <ImproveIcon fontSize="small" />
        </IconButton>
      </Tooltip>}

      <Tooltip title="Explain" placement="top">
        <IconButton 
          size="small" 
          onClick={(e) => {
            e.stopPropagation();
            if (onExplain) onExplain(item, categoryIndex, itemIndex);
          }}
        >
          <ExplainIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      
      <Tooltip title="Copy" placement="top">
        <IconButton 
          size="small" 
          onClick={(e) => {
            e.stopPropagation();
            if (onCopy) onCopy(item, categoryIndex, itemIndex);
            else {
              navigator.clipboard.writeText(
                JSON.stringify(item, null, 2)
              );
            }
          }}
        >
          <CopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      
      <Tooltip title="Delete" placement="top">
        <IconButton 
          size="small" 
          color="error"
          onClick={(e) => {
            e.stopPropagation();
            if (onDelete) onDelete(item, categoryIndex, itemIndex);
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default ActionButtons;